import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { houseModels, configuratorOptions, houseMediaSets } from '../../data/houses';
import Navbar from '../Navbar/Navbar';
import './HouseConfigurator.css';

const HouseConfigurator = () => {
  const { houseId } = useParams();
  const navigate = useNavigate();
  const [house, setHouse] = useState(null);
  const [activeTab, setActiveTab] = useState('exterior');
  const [activeInteriorTab, setActiveInteriorTab] = useState('style');
  const interiorTabsRef = useRef(null);
  const [selectedSection, setSelectedSection] = useState(null); // For nested options: 'ground-floor', 'first-floor', 'stairs', 'elevation'
  const [selectedRoom, setSelectedRoom] = useState(null); // For room selection within sections
  const [selectedOptions, setSelectedOptions] = useState({
    exterior: 'classic',
    interior: 'standard',
    // Defaults chosen so base configurator price equals the house's startingPrice
    bedrooms: '2',
    washrooms: '1',
    kitchens: '1',
    rooftop: 'standard',
    accessories: [],
    // Nested options for 5-Marla
    'ground-floor': {},
    'first-floor': {},
    'stairs': {},
    'elevation': {}
  });
  // Slider state for the preview images
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const foundHouse = houseModels.find(h => h.id === houseId);
    if (foundHouse) {
      setHouse(foundHouse);
      // For Marla houses (5 Marla and 10 Marla), initialize with ground floor
      if (
        foundHouse.productCategory &&
        (foundHouse.productCategory.includes('5-marla') ||
          foundHouse.productCategory.includes('10-marla'))
      ) {
        setSelectedSection('ground-floor');
      }
    }
  }, [houseId]);

  useEffect(() => {
    // Scroll to active tab when it changes
    if (interiorTabsRef.current && activeTab === 'interior') {
      const tabs = ['style', 'bedrooms', 'washrooms', 'kitchens', 'rooftop'];
      const activeIndex = tabs.indexOf(activeInteriorTab);
      const activeTabElement = interiorTabsRef.current.querySelector(`.interior-sub-tab:nth-child(${activeIndex + 1})`);
      if (activeTabElement) {
        activeTabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeInteriorTab, activeTab]);

  // Reset slider to first image when the context changes
  const houseKey = house ? house.id : null;
  useEffect(() => {
    setCurrentSlide(0);
  }, [selectedSection, selectedRoom, houseKey]);

  if (!house) {
    return (
      <div className="configurator-container">
        <Navbar />
        <div className="house-not-found">
          <h2>House Not Found</h2>
          <button onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  const handleOptionChange = (category, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleInteriorOptionChange = (category, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleAccessoryToggle = (accessoryId) => {
    setSelectedOptions(prev => ({
      ...prev,
      accessories: prev.accessories.includes(accessoryId)
        ? prev.accessories.filter(id => id !== accessoryId)
        : [...prev.accessories, accessoryId]
    }));
  };

  const calculateTotalPrice = () => {
    let total = house.startingPrice;

    // Exterior options pricing
    const exteriorPrices = {
      classic: 0,
      modern: 15000,
      luxury: 30000
    };
    total += exteriorPrices[selectedOptions.exterior] || 0;

    // Interior options pricing
    const interiorPrices = {
      standard: 0,
      premium: 20000,
      luxury: 40000
    };
    total += interiorPrices[selectedOptions.interior] || 0;

    // Bedrooms pricing
    const bedroomPrices = {
      '2': 0,
      '3': 5000,
      '4': 12000,
      '5': 20000
    };
    total += bedroomPrices[selectedOptions.bedrooms] || 0;

    // Washrooms pricing
    const washroomPrices = {
      '1': 0,
      '2': 3000,
      '3': 8000,
      '4': 15000
    };
    total += washroomPrices[selectedOptions.washrooms] || 0;

    // Kitchens pricing
    const kitchenPrices = {
      '1': 0,
      '2': 10000
    };
    total += kitchenPrices[selectedOptions.kitchens] || 0;

    // Rooftop pricing
    const rooftopPrices = {
      'standard': 0,
      'covered': 15000,
      'garden': 25000
    };
    total += rooftopPrices[selectedOptions.rooftop] || 0;

    // Accessories pricing
    const accessoryPrices = {
      'smart-home': 15000,
      'solar': 25000,
      'pool': 35000,
      'garden': 12000,
      'security': 8000,
      'garage': 20000,
      'premium-doors': 8000,
      'door-locks': 3000,
      'windows': 12000,
      'flooring': 15000,
      'lighting': 6000
    };

    selectedOptions.accessories.forEach(accessoryId => {
      total += accessoryPrices[accessoryId] || 0;
    });

    return total;
  };

  const {
    exteriorOptions,
    interiorOptions,
    bedroomOptions,
    washroomOptions,
    kitchenOptions,
    rooftopOptions,
    accessories
  } = configuratorOptions;

  // Nested options structure for 5-Marla houses
  const nestedSections = [
    { id: 'ground-floor', name: 'Ground Floor', order: 1, icon: '🏠' },
    { id: 'first-floor', name: 'First Floor', order: 2, icon: '🏡' },
    { id: 'stairs', name: 'Stairs', order: 3, icon: '🪜' },
    { id: 'elevation', name: 'Elevation', order: 4, icon: '🏛️' },
    // Accessories icon adjusted to feel more like upgrades
    { id: 'accessories', name: 'Accessories', order: 5, icon: '✨' }
  ];

  // Room icons mapping
  const roomIcons = {
    'master-bed': '🛏️',
    'master-bath': '🚿',
    'living': '🛋️',
    'kitchen': '🍳',
    'bed': '🛏️',
    'bath': '🚿',
    'bed-1': '🛏️',
    'bed-2': '🛏️',
    'bath-1': '🚿',
    'bath-2': '🚿'
  };

  // Media for previews is now driven from data in `houseMediaSets` in houses.js

  const groundFloorRooms = {
    'master-bed': { name: 'Master Bed' },
    'master-bath': { name: 'Master Bath' },
    living: { name: 'Living' },
    kitchen: { name: 'Kitchen' }
  };

  const firstFloorRooms = {
    'bed-1': { name: 'Bed 1' },
    'bed-2': { name: 'Bed 2' },
    'bath-1': { name: 'Bath 1' },
    'bath-2': { name: 'Bath 2' }
  };

  // Stairs options - simple selection
  const stairsOptions = [
    { id: 'basic', name: 'Basic', description: 'Standard stairs design', price: 0 },
    { id: 'modern', name: 'Modern', description: 'Contemporary stairs design', price: 5000 },
    { id: 'elegant', name: 'Elegant', description: 'Elegant stairs design', price: 10000 }
  ];

  // Elevation options (3 styles) with cover images for the cards
  const elevationOptions = [
    {
      id: 'classic',
      name: 'Classic',
      description: 'Timeless, elegant classical elevation with balanced proportions.',
      price: 0,
      coverImage: houseMediaSets[house?.id]?.elevationClassic?.[1] || house?.image
    },
    {
      id: 'modern',
      name: 'Modern',
      description: 'Clean lines, large glazing, and contemporary materials.',
      price: 15000,
      coverImage: houseMediaSets[house?.id]?.elevationModern?.[3] || house?.image
    },
    {
      id: 'ultra-modern',
      name: 'Ultra-Modern',
      description: 'Bold geometry, premium finishes, and statement lighting.',
      price: 30000,
      coverImage: houseMediaSets[house?.id]?.elevationUltraModern?.[1] || house?.image
    }
  ];

  // Check if this house should use the Marla nested layout (5 Marla and 10 Marla)
  const isMarlaLayout =
    house &&
    house.productCategory &&
    (house.productCategory.includes('5-marla') ||
      house.productCategory.includes('10-marla'));

  // Decide which images to show in the preview slider based on the current selection
  const getPreviewConfig = () => {
    if (!house) {
      return { images: [], title: '', description: '' };
    }

    // For non-Marla houses, just show the main house image
    if (!isMarlaLayout) {
      return {
        images: [house.image],
        title: house.title,
        description: house.description,
      };
    }

    // Pick media set for this specific house (falls back to base)
    const mediaKey = house.mediaKey || house.id;
    const media = houseMediaSets[mediaKey] || {};

    // Marla-specific image sets
    // Stairs slider
    if (selectedSection === 'stairs' && media.stairs && media.stairs.length > 0) {
      return {
        images: media.stairs,
        title: 'Stairs Views',
        description: 'Different views of the stairs.'
      };
    }

    // Elevation slider: change media based on which elevation is selected
    if (selectedSection === 'elevation') {
      const selectedElevationId = selectedOptions['elevation']?.selected || 'classic';

      if (selectedElevationId === 'classic' && media.elevationClassic && media.elevationClassic.length > 0) {
        return {
          images: media.elevationClassic,
          title: 'Classic Elevation',
          description: 'Classic exterior elevation views.'
        };
      }

      if (selectedElevationId === 'modern' && media.elevationModern && media.elevationModern.length > 0) {
        return {
          images: media.elevationModern,
          title: 'Modern Elevation',
          description: 'Modern exterior elevation views.'
        };
      }

      if (selectedElevationId === 'ultra-modern' && media.elevationUltraModern && media.elevationUltraModern.length > 0) {
        return {
          images: media.elevationUltraModern,
          title: 'Ultra-Modern Elevation',
          description: 'Ultra-modern exterior elevation views.'
        };
      }
    }

    // First floor room-specific sliders (placeholder images)
    if (selectedSection === 'first-floor') {
      // Any "bed-*" room uses the shared first floor bed media
      if (
        selectedRoom &&
        selectedRoom.startsWith('bed') &&
        media.firstFloorBed &&
        media.firstFloorBed.length > 0
      ) {
        return {
          images: media.firstFloorBed,
          title: 'First Floor - Bed',
          description: house.description
        };
      }

      // Any "bath-*" room uses the shared first floor bath media
      if (
        selectedRoom &&
        selectedRoom.startsWith('bath') &&
        media.firstFloorBath &&
        media.firstFloorBath.length > 0
      ) {
        return {
          images: media.firstFloorBath,
          title: 'First Floor - Bath',
          description: house.description
        };
      }

      // Fallback for first floor: general front elevation media
      if (media.firstFloorFrontend && media.firstFloorFrontend.length > 0) {
        return {
          images: media.firstFloorFrontend,
          title: 'First Floor - Front Elevation',
          description: house.description
        };
      }
    }

    // Ground floor master bed slider
    if (
      selectedSection === 'ground-floor' &&
      selectedRoom === 'master-bed' &&
      media.groundFloorMasterBed &&
      media.groundFloorMasterBed.length > 0
    ) {
      return {
        images: media.groundFloorMasterBed,
        title: 'Ground Floor - Master Bed',
        description: house.description
      };
    }

    // Ground floor master bath slider (placeholder images)
    if (
      selectedSection === 'ground-floor' &&
      selectedRoom === 'master-bath' &&
      media.groundFloorMasterBath &&
      media.groundFloorMasterBath.length > 0
    ) {
      return {
        images: media.groundFloorMasterBath,
        title: 'Ground Floor - Master Bath',
        description: house.description
      };
    }

    // Ground floor living slider (placeholder images)
    if (
      selectedSection === 'ground-floor' &&
      selectedRoom === 'living' &&
      media.groundFloorLiving &&
      media.groundFloorLiving.length > 0
    ) {
      return {
        images: media.groundFloorLiving,
        title: 'Ground Floor - Living',
        description: house.description
      };
    }

    // Ground floor kitchen slider (placeholder images)
    if (
      selectedSection === 'ground-floor' &&
      selectedRoom === 'kitchen' &&
      media.groundFloorKitchen &&
      media.groundFloorKitchen.length > 0
    ) {
      return {
        images: media.groundFloorKitchen,
        title: 'Ground Floor - Kitchen',
        description: house.description
      };
    }

    // Ground floor interior slider (general interior)
    if (selectedSection === 'ground-floor' && media.groundFloorInterior && media.groundFloorInterior.length > 0) {
      return {
        images: media.groundFloorInterior,
        title: 'Ground Floor Interior',
        description: house.description
      };
    }

    // Fallback: use the main house image
    return {
      images: [house.image],
      title: house.title,
      description: house.description
    };
  };

  const handleStairsOptionChange = (stairsId) => {
    setSelectedOptions(prev => {
      const currentSelected = prev['stairs']?.selected;
      // If clicking the same option, uncheck it
      const newSelected = currentSelected === stairsId ? null : stairsId;

      if (newSelected === null) {
        // Remove the selected property
        const updatedStairs = { ...prev['stairs'] };
        delete updatedStairs.selected;

        // If stairs object is empty, remove it
        if (Object.keys(updatedStairs).length === 0) {
          const updated = { ...prev };
          delete updated['stairs'];
          return updated;
        }

        return {
          ...prev,
          'stairs': updatedStairs
        };
      }

      return {
        ...prev,
        'stairs': {
          ...prev['stairs'],
          selected: newSelected
        }
      };
    });
  };

  const handleElevationChange = (elevationId) => {
    setSelectedOptions(prev => {
      const currentSelected = prev['elevation']?.selected;
      // If clicking the same option, uncheck it
      const newSelected = currentSelected === elevationId ? null : elevationId;

      if (newSelected === null) {
        // Remove the selected property
        const updatedElevation = { ...prev['elevation'] };
        delete updatedElevation.selected;

        // If elevation object is empty, remove it
        if (Object.keys(updatedElevation).length === 0) {
          const updated = { ...prev };
          delete updated['elevation'];
          return updated;
        }

        return {
          ...prev,
          'elevation': updatedElevation
        };
      }

      return {
        ...prev,
        'elevation': {
          ...prev['elevation'],
          selected: newSelected
        }
      };
    });
  };

  return (
    <div className="configurator-container">

      <div className="configurator-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back
        </button>
        <div className="house-name">{house.name}</div>
      </div>

      {isMarlaLayout ? (
        <div className="nested-sections-tabs">
          <div className="nested-sections-container">
            {nestedSections.map((section, index) => (
              <button
                key={section.id}
                className={`nested-section-tab ${selectedSection === section.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedSection(section.id);
                  setSelectedRoom(null);
                }}
              >
                <span className="section-icon">{section.icon}</span>
                <span className="section-name">{section.name}</span>
                {selectedSection === section.id && <div className="section-indicator"></div>}
              </button>
            ))}
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((nestedSections.findIndex(s => s.id === selectedSection) + 1) / nestedSections.length) * 100}%` }}
            ></div>
          </div>
        </div>
      ) : (
        <div className="configurator-tabs">
          <button
            className={`tab-button ${activeTab === 'exterior' ? 'active' : ''}`}
            onClick={() => setActiveTab('exterior')}
          >
            Exterior
          </button>
          <button
            className={`tab-button ${activeTab === 'interior' ? 'active' : ''}`}
            onClick={() => setActiveTab('interior')}
          >
            Interior
          </button>
          <button
            className={`tab-button ${activeTab === 'accessories' ? 'active' : ''}`}
            onClick={() => setActiveTab('accessories')}
          >
            Accessories
          </button>
        </div>
      )}

      <div className="configurator-main">
        <div className={`configurator-sidebar ${isMarlaLayout && selectedSection === 'accessories' ? 'full-width' : ''}`}>
          <div className="sidebar-content">
            {!isMarlaLayout && <h2 className="sidebar-title">Choose your look</h2>}

            {isMarlaLayout && selectedSection === 'ground-floor' && (
              <div className="nested-options-container">
                <div className="section-header">
                  <h3 className="section-subtitle">Ground Floor</h3>
                  <p className="section-description">Select a room to view photos</p>
                </div>
                <div className="rooms-grid">
                  {Object.keys(groundFloorRooms).map(roomId => {
                    const room = groundFloorRooms[roomId];
                    const isSelected = selectedRoom === roomId;
                    return (
                      <div key={roomId} className={`room-card ${isSelected ? 'active' : ''}`}>
                        <button
                          className="room-card-button"
                          onClick={() => setSelectedRoom(isSelected ? null : roomId)}
                        >
                          <div className="room-icon">{roomIcons[roomId] || '🏠'}</div>
                          <div className="room-name">{room.name}</div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isMarlaLayout && selectedSection === 'first-floor' && (
              <div className="nested-options-container">
                <div className="section-header">
                  <h3 className="section-subtitle">First Floor</h3>
                  <p className="section-description">Select a room to view photos</p>
                </div>
                <div className="rooms-grid">
                  {Object.keys(firstFloorRooms).map(roomId => {
                    const room = firstFloorRooms[roomId];
                    const isSelected = selectedRoom === roomId;
                    return (
                      <div key={roomId} className={`room-card ${isSelected ? 'active' : ''}`}>
                        <button
                          className="room-card-button"
                          onClick={() => setSelectedRoom(isSelected ? null : roomId)}
                        >
                          <div className="room-icon">{roomIcons[roomId] || '🏠'}</div>
                          <div className="room-name">{room.name}</div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isMarlaLayout && selectedSection === 'stairs' && (
              <div className="nested-options-container">
                <div className="section-header">
                  <h3 className="section-subtitle">Choose Stairs Design</h3>
                  <p className="section-description">Select your preferred stairs style</p>
                </div>
                <div className="stairs-options-grid">
                  {stairsOptions.map(option => {
                    const isSelected = selectedOptions['stairs']?.selected === option.id;
                    return (
                      <div
                        key={option.id}
                        className={`stairs-option-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleStairsOptionChange(option.id)}
                      >
                        <div className="stairs-card-icon">🪜</div>
                        <div className="stairs-card-content">
                          <h4 className="stairs-card-name">{option.name}</h4>
                          <p className="stairs-card-description">{option.description}</p>
                          {option.price > 0 && (
                            <div className="stairs-card-price">+${option.price.toLocaleString()}</div>
                          )}
                        </div>
                        {isSelected && (
                          <div className="stairs-selected-indicator">
                            <span>✓</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isMarlaLayout && selectedSection === 'elevation' && (
              <div className="nested-options-container">
                <div className="section-header">
                  <h3 className="section-subtitle">Choose Elevation Style</h3>
                  <p className="section-description">Select your preferred exterior elevation design</p>
                </div>
                <div className="elevation-options-grid">
                  {elevationOptions.map(option => {
                    const isSelected = selectedOptions['elevation']?.selected === option.id;
                    return (
                      <div
                        key={option.id}
                        className={`elevation-option-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleElevationChange(option.id)}
                      >
                        <div className="elevation-card-preview">
                          <img
                            src={option.coverImage}
                            alt={option.name}
                            className="elevation-card-image"
                          />
                          <div className="elevation-card-overlay"></div>
                          <div className="elevation-card-icon">🏛️</div>
                        </div>
                        <div className="elevation-card-content">
                          <h4 className="elevation-card-name">{option.name}</h4>
                          <p className="elevation-card-description">{option.description}</p>
                          {option.price > 0 && (
                            <div className="elevation-card-price">+${option.price.toLocaleString()}</div>
                          )}
                        </div>
                        {isSelected && (
                          <div className="elevation-selected-indicator">
                            <span>✓</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isMarlaLayout && selectedSection === 'accessories' && (
              <div className="nested-options-container">
                <div className="section-header">
                  <h3 className="section-subtitle">Choose Accessories</h3>
                  <p className="section-description">Add premium features to your 5 Marla home</p>
                </div>
                <div className="accessories-grid">
                  {accessories.map(accessory => {
                    const isSelected = selectedOptions.accessories.includes(accessory.id);
                    return (
                      <div
                        key={accessory.id}
                        className={`accessory-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleAccessoryToggle(accessory.id)}
                      >
                        <div className="accessory-image-container">
                          <img src={accessory.image} alt={accessory.name} className="accessory-image" />
                          {isSelected && (
                            <div className="accessory-checkmark">
                              <span>✓</span>
                            </div>
                          )}
                        </div>
                        <div className="accessory-card-content">
                          <h4 className="accessory-card-name">{accessory.name}</h4>
                          <div className="accessory-card-price">+${accessory.price.toLocaleString()}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!isMarlaLayout && activeTab === 'exterior' && (
              <div className="options-list">
                {exteriorOptions.map(option => (
                  <div
                    key={option.id}
                    className={`option-item ${selectedOptions.exterior === option.id ? 'selected' : ''}`}
                    onClick={() => handleOptionChange('exterior', option.id)}
                  >
                    <div className="option-indicator"></div>
                    <div className="option-details">
                      <div className="option-header">
                        <div className="option-preview-circle" style={{ background: option.id === 'classic' ? 'linear-gradient(135deg, #8b7355 0%, #6b5d4f 100%)' : option.id === 'modern' ? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)' : 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)' }}></div>
                        <div className="option-info">
                          <h3 className="option-name">{option.name}</h3>
                          {option.price > 0 && (
                            <div className="option-price">${option.price.toLocaleString()}</div>
                          )}
                        </div>
                      </div>
                      <div className="option-features">
                        {option.features.map((feature, idx) => (
                          <div key={idx} className="feature-item-text">{feature}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isMarlaLayout && activeTab === 'interior' && (
              <div className="interior-tabs-container">
                <div className="interior-sub-tabs-wrapper">
                  <button
                    className="interior-nav-arrow interior-nav-arrow-left"
                    onClick={() => {
                      const tabs = ['style', 'bedrooms', 'washrooms', 'kitchens', 'rooftop'];
                      const currentIndex = tabs.indexOf(activeInteriorTab);
                      if (currentIndex > 0) {
                        const newTab = tabs[currentIndex - 1];
                        setActiveInteriorTab(newTab);
                        // Scroll to the active tab
                        setTimeout(() => {
                          if (interiorTabsRef.current) {
                            const activeTabElement = interiorTabsRef.current.querySelector(`.interior-sub-tab:nth-child(${tabs.indexOf(newTab) + 1})`);
                            if (activeTabElement) {
                              activeTabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                            }
                          }
                        }, 0);
                      }
                    }}
                  >
                    ←
                  </button>
                  <div className="interior-sub-tabs" ref={interiorTabsRef}>
                    <button
                      className={`interior-sub-tab ${activeInteriorTab === 'style' ? 'active' : ''}`}
                      onClick={() => setActiveInteriorTab('style')}
                    >
                      Style
                    </button>
                    <button
                      className={`interior-sub-tab ${activeInteriorTab === 'bedrooms' ? 'active' : ''}`}
                      onClick={() => setActiveInteriorTab('bedrooms')}
                    >
                      Bedrooms
                    </button>
                    <button
                      className={`interior-sub-tab ${activeInteriorTab === 'washrooms' ? 'active' : ''}`}
                      onClick={() => setActiveInteriorTab('washrooms')}
                    >
                      Washrooms
                    </button>
                    <button
                      className={`interior-sub-tab ${activeInteriorTab === 'kitchens' ? 'active' : ''}`}
                      onClick={() => setActiveInteriorTab('kitchens')}
                    >
                      Kitchens
                    </button>
                    <button
                      className={`interior-sub-tab ${activeInteriorTab === 'rooftop' ? 'active' : ''}`}
                      onClick={() => setActiveInteriorTab('rooftop')}
                    >
                      Rooftop
                    </button>
                  </div>
                  <button
                    className="interior-nav-arrow interior-nav-arrow-right"
                    onClick={() => {
                      const tabs = ['style', 'bedrooms', 'washrooms', 'kitchens', 'rooftop'];
                      const currentIndex = tabs.indexOf(activeInteriorTab);
                      if (currentIndex < tabs.length - 1) {
                        const newTab = tabs[currentIndex + 1];
                        setActiveInteriorTab(newTab);
                        // Scroll to the active tab
                        setTimeout(() => {
                          if (interiorTabsRef.current) {
                            const activeTabElement = interiorTabsRef.current.querySelector(`.interior-sub-tab:nth-child(${tabs.indexOf(newTab) + 1})`);
                            if (activeTabElement) {
                              activeTabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                            }
                          }
                        }, 0);
                      }
                    }}
                  >
                    →
                  </button>
                </div>

                {activeInteriorTab === 'style' && (
                  <div className="options-list">
                    {interiorOptions.map(option => (
                      <div
                        key={option.id}
                        className={`option-item ${selectedOptions.interior === option.id ? 'selected' : ''}`}
                        onClick={() => handleOptionChange('interior', option.id)}
                      >
                        <div className="option-indicator"></div>
                        <div className="option-details">
                          <div className="option-header">
                            <div className="option-preview-circle" style={{ background: option.id === 'standard' ? 'linear-gradient(135deg, #f5f5dc 0%, #e8e8d8 100%)' : option.id === 'premium' ? 'linear-gradient(135deg, #d2b48c 0%, #c19a6b 100%)' : 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)' }}></div>
                            <div className="option-info">
                              <h3 className="option-name">{option.name}</h3>
                              {option.price > 0 && (
                                <div className="option-price">${option.price.toLocaleString()}</div>
                              )}
                            </div>
                          </div>
                          <div className="option-features">
                            {option.features.map((feature, idx) => (
                              <div key={idx} className="feature-item-text">{feature}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeInteriorTab === 'bedrooms' && (
                  <div className="options-list">
                    {bedroomOptions.map(option => (
                      <div
                        key={option.id}
                        className={`option-item ${selectedOptions.bedrooms === option.id ? 'selected' : ''}`}
                        onClick={() => handleInteriorOptionChange('bedrooms', option.id)}
                      >
                        <div className="option-indicator"></div>
                        <div className="option-details">
                          <div className="option-header">
                            <div className="option-preview-circle" style={{ background: 'linear-gradient(135deg, #e8e8e8 0%, #d0d0d0 100%)' }}></div>
                            <div className="option-info">
                              <h3 className="option-name">{option.name}</h3>
                              {option.price > 0 && (
                                <div className="option-price">+${option.price.toLocaleString()}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeInteriorTab === 'washrooms' && (
                  <div className="options-list">
                    {washroomOptions.map(option => (
                      <div
                        key={option.id}
                        className={`option-item ${selectedOptions.washrooms === option.id ? 'selected' : ''}`}
                        onClick={() => handleInteriorOptionChange('washrooms', option.id)}
                      >
                        <div className="option-indicator"></div>
                        <div className="option-details">
                          <div className="option-header">
                            <div className="option-preview-circle" style={{ background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)' }}></div>
                            <div className="option-info">
                              <h3 className="option-name">{option.name}</h3>
                              {option.price > 0 && (
                                <div className="option-price">+${option.price.toLocaleString()}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeInteriorTab === 'kitchens' && (
                  <div className="options-list">
                    {kitchenOptions.map(option => (
                      <div
                        key={option.id}
                        className={`option-item ${selectedOptions.kitchens === option.id ? 'selected' : ''}`}
                        onClick={() => handleInteriorOptionChange('kitchens', option.id)}
                      >
                        <div className="option-indicator"></div>
                        <div className="option-details">
                          <div className="option-header">
                            <div className="option-preview-circle" style={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)' }}></div>
                            <div className="option-info">
                              <h3 className="option-name">{option.name}</h3>
                              {option.price > 0 && (
                                <div className="option-price">+${option.price.toLocaleString()}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeInteriorTab === 'rooftop' && (
                  <div className="options-list">
                    {rooftopOptions.map(option => (
                      <div
                        key={option.id}
                        className={`option-item ${selectedOptions.rooftop === option.id ? 'selected' : ''}`}
                        onClick={() => handleInteriorOptionChange('rooftop', option.id)}
                      >
                        <div className="option-indicator"></div>
                        <div className="option-details">
                          <div className="option-header">
                            <div className="option-preview-circle" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}></div>
                            <div className="option-info">
                              <h3 className="option-name">{option.name}</h3>
                              {option.price > 0 && (
                                <div className="option-price">+${option.price.toLocaleString()}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!isMarlaLayout && activeTab === 'accessories' && (
              <div className="accessories-grid">
                {accessories.map(accessory => {
                  const isSelected = selectedOptions.accessories.includes(accessory.id);
                  return (
                    <div
                      key={accessory.id}
                      className={`accessory-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleAccessoryToggle(accessory.id)}
                    >
                      <div className="accessory-image-container">
                        <img src={accessory.image} alt={accessory.name} className="accessory-image" />
                        {isSelected && (
                          <div className="accessory-checkmark">
                            <span>✓</span>
                          </div>
                        )}
                      </div>
                      <div className="accessory-card-content">
                        <h4 className="accessory-card-name">{accessory.name}</h4>
                        <div className="accessory-card-price">+${accessory.price.toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="sidebar-footer">
            <div className="price-section">
              <div className="price-label">MSRP AS BUILT</div>
              <div className="price-amount">${calculateTotalPrice().toLocaleString()}</div>
            </div>
            <button className="next-button">
              Next →
            </button>
          </div>
        </div>

        {!isMarlaLayout || selectedSection !== 'accessories' ? (
          <div className="configurator-preview">
            <div className="preview-container">
              {(() => {
                const previewConfig = getPreviewConfig();
                const mediaItems = previewConfig.images && previewConfig.images.length > 0
                  ? previewConfig.images
                  : [house.image];
                const safeIndex = Math.min(currentSlide, mediaItems.length - 1);
                const hasMultipleSlides = mediaItems.length > 1;
                const currentItem = mediaItems[safeIndex];
                const isVideo = currentItem && typeof currentItem === 'object' && currentItem.type === 'video';
                const mediaSrc = isVideo ? currentItem.src : currentItem;

                const handlePrevSlide = () => {
                  if (!hasMultipleSlides) return;
                  setCurrentSlide(prev => (prev - 1 + mediaItems.length) % mediaItems.length);
                };

                const handleNextSlide = () => {
                  if (!hasMultipleSlides) return;
                  setCurrentSlide(prev => (prev + 1) % mediaItems.length);
                };

                return (
                  <>
                    <div className="preview-slider">
                      {hasMultipleSlides && (
                        <button
                          type="button"
                          className="slider-arrow slider-arrow-left"
                          onClick={handlePrevSlide}
                          aria-label="Previous"
                        >
                          ‹
                        </button>
                      )}

                      {isVideo ? (
                        <video
                          key={currentSlide}
                          src={mediaSrc}
                          className="preview-image preview-video"
                          autoPlay
                          muted
                          loop
                          playsInline
                          controls
                        />
                      ) : (
                        <img
                          src={mediaSrc}
                          alt={previewConfig.title || house.title}
                          className="preview-image"
                        />
                      )}

                      {hasMultipleSlides && (
                        <button
                          type="button"
                          className="slider-arrow slider-arrow-right"
                          onClick={handleNextSlide}
                          aria-label="Next"
                        >
                          ›
                        </button>
                      )}
                    </div>

                    {hasMultipleSlides && (
                      <div className="slider-dots">
                        {mediaItems.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            className={`slider-dot ${index === safeIndex ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}

                    <div className="preview-caption">
                      {previewConfig.title && (
                        <div className="preview-caption-title">{previewConfig.title}</div>
                      )}
                      <div className="preview-caption-text">
                        {previewConfig.description || house.description}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HouseConfigurator;
